/**
 * iter351 — L'atelier déclare les pièces et consommables utilisés.
 * Le catalogue est affiché SANS prix ; les tarifs sont appliqués côté serveur.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Package, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export const ConsommablesDialog = ({ open, onClose, orId, onDone }) => {
  const [q, setQ] = useState("");
  const [catalogue, setCatalogue] = useState([]);
  const [panier, setPanier] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      api.get("/atelier/articles", { params: { q } })
        .then(({ data }) => setCatalogue(data.items || []))
        .catch(() => setCatalogue([]));
    }, 250);
    return () => clearTimeout(t);
  }, [open, q]);

  const ajouter = (art) => {
    setPanier((p) => (p.some((x) => x.article_id === art.id)
      ? p.map((x) => (x.article_id === art.id ? { ...x, quantite: x.quantite + 1 } : x))
      : [...p, { article_id: art.id, libelle: art.libelle, unite: art.unite, quantite: 1 }]));
  };

  // iter353 — Consommable libre : ce qui n'est pas au catalogue se saisit à la main
  const ajouterLibre = () => {
    const libelle = q.trim();
    if (!libelle) { toast.error("Écris d'abord le nom du consommable"); return; }
    setPanier((p) => [...p, { article_id: null, libelle, quantite: 1, libre: true }]);
    setQ("");
  };

  const valider = async () => {
    const articles = panier.filter((p) => p.libelle.trim() && p.quantite > 0);
    if (articles.length === 0) { toast.error("Ajoute au moins une pièce"); return; }
    setBusy(true);
    try {
      await api.post(`/atelier/travail/${orId}/consommables`, { articles });
      toast.success("Pièces enregistrées sur le dossier");
      setPanier([]); setQ("");
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="consommables-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Package className="w-4 h-4 text-[#52a675]" /> Pièces et consommables utilisés
          </DialogTitle>
        </DialogHeader>

        <Input
          value={q} onChange={(e) => setQ(e.target.value)} placeholder="Chercher ou nommer un consommable…"
          className="h-11" data-testid="consommables-recherche"
        />

        {q.trim() && (
          <button
            onClick={ajouterLibre}
            className="text-xs text-[#2d5f3f] inline-flex items-center gap-1 hover:underline self-start"
            data-testid="consommable-libre"
          >
            <Plus className="w-3.5 h-3.5" /> Ajouter « {q.trim()} » comme consommable libre
          </button>
        )}

        <div className="max-h-44 overflow-y-auto space-y-1" data-testid="consommables-catalogue">
          {catalogue.map((a) => (
            <button
              key={a.id} onClick={() => ajouter(a)}
              className="w-full text-left px-3 py-2 rounded-lg border border-gray-200 hover:border-[#52a675] text-sm flex items-center gap-2"
              data-testid={`consommable-${a.id}`}
            >
              <Plus className="w-3.5 h-3.5 text-[#52a675] flex-shrink-0" />
              <span className="truncate flex-1">{a.libelle}</span>
              {a.unite && <span className="text-[11px] text-gray-400">{a.unite}</span>}
            </button>
          ))}
          {catalogue.length === 0 && (
            <div className="text-xs text-gray-400 text-center py-3">Aucun article trouvé</div>
          )}
        </div>

        {panier.length > 0 && (
          <div className="border-t border-gray-100 pt-2 space-y-1.5" data-testid="consommables-panier">
            {panier.map((p, idx) => (
              <div key={p.article_id || idx} className="flex items-center gap-1.5" data-testid={`panier-${idx}`}>
                <span className="text-sm flex-1 truncate">
                  {p.libelle}
                  {p.libre && <span className="text-[10px] text-orange-700 ml-1">(libre)</span>}
                </span>
                <Input
                  type="number" min="0" step="0.5" value={p.quantite}
                  onChange={(e) => setPanier(panier.map((x, i) => (i === idx ? { ...x, quantite: parseFloat(e.target.value) || 0 } : x)))}
                  className="h-9 w-20" data-testid={`panier-qte-${idx}`}
                />
                <Button
                  variant="ghost" size="icon" className="h-9 w-8"
                  onClick={() => setPanier(panier.filter((_, i) => i !== idx))}
                  data-testid={`panier-retirer-${idx}`}
                >
                  <X className="w-4 h-4 text-gray-400" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="consommables-annuler">Annuler</Button>
          <Button onClick={valider} disabled={busy} className="bg-[#52a675] hover:bg-[#2d5f3f]" data-testid="consommables-valider">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConsommablesDialog;
